import apiClient from './client';

/**
 * Fetch all decks for the current user
 * @returns {Promise<Array>} Array of deck objects
 */
export const fetchDecks = async () => {
  const response = await apiClient.get('/api/decks/');
  return response.data;
};

/**
 * Fetch all available domains for the current user
 * @returns {Promise<Array>} Array of domain objects with name, color, and topic_count
 */
export const fetchDomains = async () => {
  const response = await apiClient.get('/api/decks/domains/list');
  return response.data;
};

/**
 * Update domain (and optionally topic) for a deck
 * @param {string} deckId - Deck ID
 * @param {object} updates - Updates to apply { domain?, topic?, domain_color?, topic_color? }
 * @returns {Promise<object>} Updated deck details
 */
export const updateDeckDomain = async (deckId, updates) => {
  const response = await apiClient.put(`/api/decks/${deckId}/meta`, updates);
  return response.data;
};

/**
 * Fetch a single slide from a deck
 * @param {string} deckId - The deck ID
 * @param {number} slideNumber - The slide number (1-indexed)
 * @returns {Promise<object>} Slide data with structured_schema for v2.0 slides
 */
export const getSlide = async (deckId, slideNumber) => {
  const response = await apiClient.get(`/api/decks/${deckId}/slides/${slideNumber}`);
  return response.data;
};

/**
 * Upload a deck for Lab testing (admin-only, no auto-parsing)
 * @param {File} file - PowerPoint file to upload
 * @returns {Promise<object>} Created deck details
 */
export const labUploadDeck = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/api/lab/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

  return response.data;
};

/**
 * Fetch a slide's original image as a blob URL (admin-only, lab endpoint)
 * @param {string} deckId - Deck ID
 * @param {number} slideNumber - Slide number (1-indexed)
 * @returns {Promise<string>} Object URL for the image blob
 */
export const getSlideImageBlob = async (deckId, slideNumber) => {
  const response = await apiClient.get(
    `/api/lab/slide/${deckId}/${slideNumber}/image`,
    { responseType: 'blob' }
  );
  return URL.createObjectURL(response.data);
};

/**
 * Fetch a slide's original image as a blob URL (public endpoint, any deck owner)
 * @param {string} deckId - Deck ID
 * @param {number} slideNumber - Slide number (1-indexed)
 * @returns {Promise<string>} Object URL for the image blob
 */
export const getSlideImagePublic = async (deckId, slideNumber) => {
  const response = await apiClient.get(
    `/api/decks/${deckId}/slides/${slideNumber}/thumbnail`,
    { responseType: 'blob' }
  );
  return URL.createObjectURL(response.data);
};

/**
 * Fetch raw slide data for Lab inspection (filtered, no embeddings)
 * @param {string} deckId - Deck ID
 * @param {number} slideNumber - Slide number (1-indexed)
 * @returns {Promise<object>} Filtered slide data object
 */
export const getSlideRawData = async (deckId, slideNumber) => {
  const response = await apiClient.get(`/api/lab/slide/${deckId}/${slideNumber}/raw-data`);
  return response.data;
};

/**
 * Run standardization pipeline for a single slide (admin-only, Lab)
 * @param {string} deckId - Deck ID
 * @param {number} slideNumber - Slide number (1-indexed)
 * @param {string|null} testCaseId - Optional test case ID to link this run
 * @returns {Promise<object>} Pipeline data with system_prompt, request_content, llm_response, standardized_content, metadata
 */
export const standardizeSlide = async (deckId, slideNumber, testCaseId = null, model = null) => {
  const body = { deck_id: deckId, slide_number: slideNumber };
  if (testCaseId) body.test_case_id = testCaseId;
  if (model) body.model = model;
  const response = await apiClient.post('/api/lab/standardize-slide', body, {
    timeout: 180000, // 3 minutes - LLM vision inference can be slow on complex slides
  });
  return response.data;
};

/**
 * Run both models (Qwen + GPT-5 Nano) on a deck for comparison (admin-only, Lab)
 * @param {string} deckId - Deck ID
 * @param {number|null} slideStart - Start slide (1-indexed, default: 1)
 * @param {number|null} slideEnd - End slide (default: slide_count)
 * @returns {Promise<object>} { results: [...], summary: { qwen: {...}, gpt5_nano: {...} } }
 */
export const compareModels = async (deckId, slideStart = null, slideEnd = null) => {
  const body = { deck_id: deckId };
  if (slideStart) body.slide_start = slideStart;
  if (slideEnd) body.slide_end = slideEnd;
  const response = await apiClient.post('/api/lab/compare-models', body, {
    timeout: 600000, // 10 minutes - runs both models on every slide
  });
  return response.data;
};

/**
 * Create a new test case (admin-only, Lab)
 * @param {object} data - Test case data { name, deck_id, slide_number, expected_threshold, notes? }
 * @returns {Promise<object>} Created test case
 */
export const createTestCase = async (data) => {
  const response = await apiClient.post('/api/lab/test-cases', data);
  return response.data;
};

/**
 * Fetch all test cases for current admin user
 * @returns {Promise<Array>} Array of test cases
 */
export const fetchTestCases = async () => {
  const response = await apiClient.get('/api/lab/test-cases');
  return response.data;
};

/**
 * Update an existing test case (admin-only, Lab)
 * @param {string} testCaseId - Test case ID
 * @param {object} updates - Partial fields to update
 * @returns {Promise<object>} Updated test case
 */
export const updateTestCase = async (testCaseId, updates) => {
  const response = await apiClient.put(`/api/lab/test-cases/${testCaseId}`, updates);
  return response.data;
};

/**
 * Delete a test case (admin-only, Lab)
 * @param {string} testCaseId - Test case ID
 * @returns {Promise<void>}
 */
export const deleteTestCase = async (testCaseId) => {
  const response = await apiClient.delete(`/api/lab/test-cases/${testCaseId}`);
  return response.data;
};

/**
 * Fetch history of past runs for a test case (admin-only, Lab)
 * @param {string} testCaseId - Test case ID
 * @param {number} limit - Maximum number of runs to fetch (default: 20)
 * @returns {Promise<Array>} Array of past run records
 */
export const fetchTestCaseHistory = async (testCaseId, limit = 20) => {
  const response = await apiClient.get(`/api/lab/test-cases/${testCaseId}/history?limit=${limit}`);
  return response.data;
};

/**
 * Update a slide's structured content (user edit)
 * @param {string} deckId - Deck ID
 * @param {number} slideNumber - Slide number (1-indexed)
 * @param {object} structuredSchema - The updated structured_schema object
 * @returns {Promise<object>} Edit result with success, edited_by, edited_at
 */
export const updateSlide = async (deckId, slideNumber, structuredSchema) => {
  const response = await apiClient.put(
    `/api/decks/${deckId}/slides/${slideNumber}`,
    { structured_schema: structuredSchema }
  );
  return response.data;
};

/**
 * Admin: Update a cached slide so all future users get the corrected version
 * @param {string} deckId - Deck ID
 * @param {number} slideNumber - Slide number (1-indexed)
 * @param {object} structuredSchema - The updated structured_schema object
 * @param {string|null} editNote - Optional note about what was changed
 * @returns {Promise<object>} Edit result with success, log_id
 */
export const adminUpdateCachedSlide = async (deckId, slideNumber, structuredSchema, editNote = null) => {
  const response = await apiClient.put('/api/decks/admin/cache/slides', {
    deck_id: deckId,
    slide_number: slideNumber,
    structured_schema: structuredSchema,
    edit_note: editNote,
  });
  return response.data;
};

/**
 * Admin: Get slide edit history log
 * @param {number} limit - Max entries to return
 * @param {string|null} deckId - Optional filter by deck
 * @returns {Promise<Array>} Array of edit log entries
 */
export const getEditLog = async (limit = 50, deckId = null) => {
  const params = { limit };
  if (deckId) params.deck_id = deckId;
  const response = await apiClient.get('/api/decks/admin/cache/edit-log', { params });
  return response.data;
};

/**
 * Admin: Get cost breakdown for a specific deck's pipeline run
 * @param {string} deckId - Deck ID
 * @returns {Promise<object>} Cost breakdown with per-phase details
 */
export const getDeckCosts = async (deckId) => {
  const response = await apiClient.get(`/api/decks/admin/costs/${deckId}`);
  return response.data;
};

/**
 * Admin: Get cost summary for recent pipeline runs
 * @param {number} limit - Max entries to return (default: 20)
 * @returns {Promise<object>} { decks: [...], count: N }
 */
export const getCostHistory = async (limit = 20) => {
  const response = await apiClient.get('/api/decks/admin/costs', { params: { limit } });
  return response.data;
};
